import { InteractiveLinkProps, InteractiveLinkWidget, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { Messages } from '../../../codegen/Localize';
import { L10n } from '../../../ts/L10n/L10n';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { CommonStyles } from '../CommonStyles';

/**
 * Contains information to render a toast with a list of actions.
 * @property message - The message to display in the toast
 * @property action - An action to call when the corresponding link is clicked
 * @property actionMessage - The action link text
 */
export interface IActionableMessageProps {
  message: string;
  actionMessage: string;
  action: () => void;
  testId: string;
}

// default action style
const defaultLinkProps = {
  href: 'javascript:;',
  style: {
    padding: '0px',
    marginLeft: TabStyles.Spacing.MediumSpace,
    color: 'inherit',
    fontWeight: 'bold',
    cursor: 'pointer',
    flexShrink: 0
  },
};

/**
 * A react component that shows a message and a list of actions
 * @param props - contains the text and actions to show
 */
export const ActionableMessage: React.SFC<IActionableMessageProps> = (props): JSX.Element => {
  // map action descriptors to interactive link props
  const linkProps = ({
      ...defaultLinkProps,
      onClick: props.action,
      text: props.message,
      testId: props.testId
    } as InteractiveLinkProps);

  // create a div with the specified message and a list of links at the end
  return (
    <div style={{
      fontWeight: 'normal',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    }}>
      {props.message}
      <span><InteractiveLinkWidget key={linkProps.testId} {...linkProps} /></span>
    </div>
  );
};

/**
 * Show an actionable toast using the specified actionable toast props
 * @param props - the props to render the actionable message. Contains the text and action props
 * @param toastType - The type of toast to show. Default is {@link ToastType.Info}
 */
export function showActionableToast(props: IActionableMessageProps, toastType: ToastType = ToastType.Info) {
  // get the toast service and show a toast with the actionable message as its content. Override
  // the actions to close the toast when they're clicked
  const service = ServiceLocator.get<ToasterService>('ToasterService');
  const toastId = service.pop({
    type: toastType,
    text: props.message,
    actionMessage: props.actionMessage,
    action: () => {
      props.action();
      service.close(toastId);
    }
  });
}

